/**
 * ScheduleStep Component
 * Step 2: Program schedule and location
 */

import React, { useState, useEffect } from 'react';
import { FaCalendarAlt, FaMapMarkerAlt, FaClock, FaPlus, FaTrash } from 'react-icons/fa';
import { DAYS_OF_WEEK, TIME_OPTIONS } from '../constants/gameFormats';
import styles from './StepStyles.module.css';
import scheduleStyles from './ScheduleStep.module.css';

const ScheduleStep = ({
  formData,
  errors,
  updateField,
  addArrayItem,
  removeArrayItem
}) => {
  // Initialize from existing data or defaults
  const [useSameTime, setUseSameTime] = useState(true);
  const [defaultStartTime, setDefaultStartTime] = useState(() => {
    return formData.schedule.frequency[0]?.startTime || '17:00';
  });
  const [defaultEndTime, setDefaultEndTime] = useState(() => {
    return formData.schedule.frequency[0]?.endTime || '19:00';
  });

  // Update default times when formData changes (for edit mode)
  useEffect(() => {
    if (formData.schedule.frequency.length > 0) {
      const firstSchedule = formData.schedule.frequency[0];
      setDefaultStartTime(firstSchedule.startTime || '17:00');
      setDefaultEndTime(firstSchedule.endTime || '19:00');
      
      // Check if all days have the same time
      const allSameTime = formData.schedule.frequency.every(
        freq => freq.startTime === firstSchedule.startTime && 
                freq.endTime === firstSchedule.endTime
      );
      setUseSameTime(allSameTime);
    }
  }, [formData.schedule.frequency.length]);

  // Toggle day selection
  const toggleDay = (day) => {
    const frequency = [...formData.schedule.frequency];
    const existingIndex = frequency.findIndex(f => f.day === day);

    if (existingIndex >= 0) {
      // Remove day
      frequency.splice(existingIndex, 1);
    } else {
      // Add day with default times
      frequency.push({
        day,
        startTime: defaultStartTime,
        endTime: defaultEndTime
      });
      // Sort by day order
      frequency.sort((a, b) => {
        const dayOrder = DAYS_OF_WEEK.map(d => d.value);
        return dayOrder.indexOf(a.day) - dayOrder.indexOf(b.day);
      });
    }

    updateField('schedule.frequency', frequency);
  };

  // Update time for a specific day
  const updateDayTime = (day, field, value) => {
    const frequency = formData.schedule.frequency.map(f => {
      if (f.day === day) {
        return { ...f, [field]: value };
      }
      return f;
    });
    updateField('schedule.frequency', frequency);
  };

  // Apply same time to all selected days
  const applyTimeToAll = (newStartTime = defaultStartTime, newEndTime = defaultEndTime) => {
    if (formData.schedule.frequency.length === 0) return;
    
    const frequency = formData.schedule.frequency.map(f => ({
      ...f,
      startTime: newStartTime,
      endTime: newEndTime
    }));
    updateField('schedule.frequency', frequency);
  };

  // Check if day is selected
  const isDaySelected = (day) => {
    return formData.schedule.frequency.some(f => f.day === day);
  };

  // Get time for a day
  const getDayTime = (day, field) => {
    const freq = formData.schedule.frequency.find(f => f.day === day);
    return freq ? freq[field] : '';
  };

  // Handle location change
  const updateLocation = (index, value) => {
    const locations = [...formData.locations];
    locations[index] = { address: value };
    updateField('locations', locations);
  };

  // Add location
  const addLocation = () => {
    updateField('locations', [...formData.locations, { address: '' }]);
  };

  // Remove location
  const removeLocation = (index) => {
    if (formData.locations.length > 1) {
      updateField('locations', formData.locations.filter((_, i) => i !== index));
    }
  };

  return (
    <div className={styles.stepContainer}>
      {/* Program Dates */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>
          <FaCalendarAlt /> Program Dates
        </h3>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label className={`${styles.label} ${styles.required}`}>
              Start Date
            </label>
            <input
              type="date"
              className={`${styles.input} ${errors['schedule.startDate'] ? styles.error : ''}`}
              value={formData.schedule.startDate}
              onChange={(e) => updateField('schedule.startDate', e.target.value)}
            />
            {errors['schedule.startDate'] && (
              <span className={styles.errorText}>{errors['schedule.startDate']}</span>
            )}
          </div>

          <div className={styles.formGroup}>
            <label className={`${styles.label} ${styles.required}`}>
              End Date
            </label>
            <input
              type="date"
              className={`${styles.input} ${errors['schedule.endDate'] ? styles.error : ''}`}
              value={formData.schedule.endDate}
              onChange={(e) => updateField('schedule.endDate', e.target.value)}
            />
            {errors['schedule.endDate'] && (
              <span className={styles.errorText}>{errors['schedule.endDate']}</span>
            )}
          </div>
        </div>
      </div>

      {/* Practice Days */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>
          <FaClock /> Practice Schedule
        </h3>

        {/* Day Selector */}
        <div className={styles.formGroup}>
          <label className={`${styles.label} ${styles.required}`}>
            Select Practice Days
          </label>
          <div className={scheduleStyles.daySelector}>
            {DAYS_OF_WEEK.map(day => (
              <button
                key={day.value}
                type="button"
                className={`${scheduleStyles.dayBtn} ${isDaySelected(day.value) ? scheduleStyles.selected : ''}`}
                onClick={() => toggleDay(day.value)}
              >
                <span className={scheduleStyles.dayLabel}>{day.label}</span>
              </button>
            ))}
          </div>
          {errors['schedule.frequency'] && (
            <span className={styles.errorText}>{errors['schedule.frequency']}</span>
          )}
        </div>

        {/* Same time toggle */}
        {formData.schedule.frequency.length > 0 && (
          <div className={scheduleStyles.sameTimeRow}>
            {formData.schedule.frequency.length > 1 && (
              <label className={scheduleStyles.sameTimeLabel}>
                <input
                  type="checkbox"
                  checked={useSameTime}
                  onChange={(e) => setUseSameTime(e.target.checked)}
                  className={scheduleStyles.sameTimeCheckbox}
                />
                <span>Same time for all days</span>
              </label>
            )}
            
            {(useSameTime || formData.schedule.frequency.length === 1) && (
              <div className={scheduleStyles.defaultTimeInputs}>
                <label className={scheduleStyles.timeLabel}>
                  {formData.schedule.frequency.length === 1 ? 'Practice Time:' : 'Time for all days:'}
                </label>
                <select
                  className={scheduleStyles.timeSelect}
                  value={defaultStartTime}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setDefaultStartTime(newValue);
                    applyTimeToAll(newValue, defaultEndTime);
                  }}
                >
                  {TIME_OPTIONS.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
                <span className={scheduleStyles.timeSeparator}>to</span>
                <select
                  className={scheduleStyles.timeSelect}
                  value={defaultEndTime}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setDefaultEndTime(newValue);
                    applyTimeToAll(defaultStartTime, newValue);
                  }}
                >
                  {TIME_OPTIONS.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}

        {/* Selected Days with Times */}
        {formData.schedule.frequency.length > 0 && (
          <div className={scheduleStyles.selectedDays}>
            {formData.schedule.frequency.map(freq => (
              <div key={freq.day} className={scheduleStyles.dayTimeRow}>
                <span className={scheduleStyles.dayName}>
                  {DAYS_OF_WEEK.find(d => d.value === freq.day)?.fullLabel}
                </span>
                
                {!useSameTime && (
                  <div className={scheduleStyles.timeInputs}>
                    <select
                      className={scheduleStyles.timeSelect}
                      value={freq.startTime}
                      onChange={(e) => updateDayTime(freq.day, 'startTime', e.target.value)}
                    >
                      {TIME_OPTIONS.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                    <span className={scheduleStyles.timeSeparator}>-</span>
                    <select
                      className={scheduleStyles.timeSelect}
                      value={freq.endTime}
                      onChange={(e) => updateDayTime(freq.day, 'endTime', e.target.value)}
                    >
                      {TIME_OPTIONS.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                )}

                {useSameTime && (
                  <span className={scheduleStyles.timeDisplay}>
                    {TIME_OPTIONS.find(t => t.value === freq.startTime)?.label || freq.startTime}
                    {' - '}
                    {TIME_OPTIONS.find(t => t.value === freq.endTime)?.label || freq.endTime}
                  </span>
                )}

                <button
                  type="button"
                  className={scheduleStyles.removeDayBtn}
                  onClick={() => toggleDay(freq.day)}
                >
                  <FaTrash />
                </button>
              </div>
            ))}
          </div>
        )}

        {formData.schedule.frequency.length === 0 && (
          <p className={scheduleStyles.emptyHint}>
            Click on the days above to select practice days
          </p>
        )}
      </div>

      {/* Locations */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>
          <FaMapMarkerAlt /> Location(s)
        </h3>

        {formData.locations.map((location, index) => (
          <div key={index} className={styles.formGroup}>
            <div className={scheduleStyles.locationRow}>
              <input
                type="text"
                className={`${styles.input} ${errors.locations && index === 0 ? styles.error : ''}`}
                value={location.address}
                onChange={(e) => updateLocation(index, e.target.value)}
                placeholder="Enter address or venue name"
              />
              {formData.locations.length > 1 && (
                <button
                  type="button"
                  className={scheduleStyles.removeLocationBtn}
                  onClick={() => removeLocation(index)}
                >
                  <FaTrash />
                </button>
              )}
            </div>
            {errors.locations && index === 0 && (
              <span className={styles.errorText}>{errors.locations}</span>
            )}
          </div>
        ))}

        <button
          type="button"
          className={styles.addItemBtn}
          onClick={addLocation}
        >
          <FaPlus /> Add Another Location
        </button>
      </div>
    </div>
  );
};

export default ScheduleStep;









